import InfiniteScroll from 'react-infinite-scroll-component';
import { Disclosure } from '@headlessui/react';
import { Role } from 'auth';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import '@/modules/common/styles/common.css';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import InvestorActivityFilters from '@/modules/investor-dashboard/components/InvestorActivityFilters.tsx';
import { InvestorDashboardWrapperOutletContext } from '@/modules/investor-dashboard/components/InvestorDashboardWrapper.tsx';
import InvestorActivityItem from '@/modules/investor-dashboard/components/items/InvestorActivityItem.tsx';
import { useMyActivitiesListQuery } from '@/modules/investor-dashboard/queries/activity.tsx';
import { EmptyListState, PageLayout, RoundBadge, Spinner } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { MyActivitiesListV1SrActivitiesMyGetParams } from 'api';

export type activityFilters = Pick<
  MyActivitiesListV1SrActivitiesMyGetParams,
  | 'type'
  | 'status'
  | 'total_from'
  | 'total_to'
  | 'register_class_id'
  | 'completion_date_from'
  | 'completion_date_to'
  | 'requested_at_date_from'
  | 'requested_at_date_to'
>;

const InvestorActivity = () => {
  const { register_id } = useOutletContext<InvestorDashboardWrapperOutletContext>();
  const [filters, setFilters] = useState<Partial<activityFilters>>({});

  const {
    data: activitiesData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useMyActivitiesListQuery({ page: 1, page_size: 15, ...filters }, Number(register_id));

  const activities = (activitiesData?.pages || []).flatMap(({ data }) => data);

  const appliedFiltersLength = Object.keys(filters).length;

  const headerAction = (
    <span>
      <Disclosure.Button>
        <div className="flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
          <img alt="filters" src={FiltersIcon} />
          <span className="text-sm">Filters</span>
          {!!appliedFiltersLength && <RoundBadge content={appliedFiltersLength} />}
        </div>
      </Disclosure.Button>
    </span>
  );

  return (
    <PageLayout
      title="Activity"
      headerAction={headerAction}
      HeaderWrapper={Disclosure}
      headerBottomContent={
        <InvestorActivityFilters showStatus filters={filters} setFilters={setFilters} />
      }
    >
      <EmptyListState
        title="No activities yet"
        description="Your transactions, payments and transfers will appear here"
        isLoading={isLoading}
        isSearchUsed={!!appliedFiltersLength}
        itemsCount={activities.length}
      />
      {isLoading && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={activities.length}
        hasMore={hasNextPage || false}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        scrollableTarget="scrollable"
      >
        <div className="w-full flex flex-col space-y-2">
          {activities.map(activity => (
            <InvestorActivityItem activity={activity} key={`${activity.type}_${activity.id}`} />
          ))}
        </div>
      </InfiniteScroll>
    </PageLayout>
  );
};

export default withPrivateAccess(InvestorActivity, Role.SR_INVESTOR);
